import Vue from 'vue';
import FloatingPanel from 'components/widgets/FloatingPanel/FloatingPanel.vue';
import InteractionMenu from 'src/components/widgets/InteractionMenu.vue';
import { scaleLinear, axisBottom, axisLeft, select, selectAll } from 'd3';
export default Vue.extend({
    components: { FloatingPanel, InteractionMenu },
    props: {
        width: { type: Number, required: true },
        height: { type: Number, required: true },
        quadColors: {
            type: Object, required: false,
            default: () => ({ topLeft: 'red', topRight: 'green', bottomLeft: 'yellow', bottomRight: 'blue' })
        },
        dots: { type: Array, required: false, default: () => [] },
        xAxis: { type: Object, required: true },
        yAxis: { type: Object, required: true },
        verticals: { type: Array, required: false, default: () => [] },
    },
    data() {
        return {
            quadrantChart: null,
            gutters: { top: 30, bottom: 60, left: 100, right: 0 },
            padding: { top: 30, bottom: 30, left: 30, right: 0 }, // Padding between the chart and the axis
        };
    },
    computed: {
        dotEls() {
            return this.dots.map(((_, index) => () => this.$refs[`dot_${index}`]?.[0]));
        },
        hoveredDot() {
            return this.dots.reduce((prev, curr) => curr.hovered ? curr : prev, null);
        },
        hoverX() {
            if (!!this.hoveredDot && this.$refs['quadrant-chart']) {
                const { left } = this.$refs['quadrant-chart'].getBoundingClientRect();
                return left + this.xScale(parseFloat(this.hoveredDot.x));
            }
            return null;
        },
        hoverY() {
            if (!!this.hoveredDot && this.$refs['quadrant-chart']) {
                const { top } = this.$refs['quadrant-chart'].getBoundingClientRect();
                return top + this.yScale(parseFloat(this.hoveredDot.y));
            }
            return null;
        },
        xScale() {
            return scaleLinear()
                .domain([this.xAxis.min, this.xAxis.max])
                .range([this.gutters.left, this.width - (this.gutters.right + 40)]);
        },
        yScale() {
            return scaleLinear()
                .domain([this.yAxis.min, this.yAxis.max])
                .range([this.height - (this.gutters.bottom + this.padding.bottom), this.gutters.top]);
        },
        chartWidth() { return Math.round(Math.abs(this.xScale(this.xAxis.min) - this.xScale(this.xAxis.max))); },
        chartHeight() { return Math.round(Math.abs(this.yScale(this.yAxis.min) - this.yScale(this.yAxis.max))); }
    },
    watch: {
        height() { this.drawAxis(); },
        width() { this.drawAxis(); },
        xAxis() { this.drawAxis(); },
        yAxis() { this.drawAxis(); },
    },
    mounted() {
        this.quadrantChart = select(this.$el.querySelector('div.svgContainer.quadrant > svg'));
        this.drawAxis();
    },
    methods: {
        verticalXPosition(value, label) {
            let labelLength;
            if (this.$refs[`text_${value}_${label}`]) {
                try {
                    labelLength = this.$refs[`text_${value}_${label}`][0].getBBox().width;
                }
                catch (e) {
                    labelLength = label.length * 8;
                }
            }
            const halfLength = labelLength / 2;
            const xPos = this.xScale(value);
            const min = this.xScale(this.xAxis.min);
            const max = this.xScale(this.xAxis.max);
            // too left
            if (xPos - halfLength < min) {
                return xPos + (min - (xPos - halfLength));
            }
            // too right
            if (xPos + halfLength > max) {
                return xPos - (xPos + halfLength - max);
            }
            // otherwise
            return this.xScale(value);
        },
        labelSide(dot) {
            if (!dot || !dot.label)
                return 'start';
            return (this.xScale(Number(dot.x)) + dot.label.length * 10) >= this.xScale(this.xAxis.max) ? 'end' : 'start';
        },
        dotClass(dot) {
            if (dot.hidden)
                return 'hidden';
            if (!this.hoveredDot)
                return 'point';
            if (!dot.hovered)
                return 'faded';
        },
        drawAxis() {
            const axes = selectAll(this.$el.querySelectorAll('.axis-colour'));
            axes.remove();
            let xAxis = axisBottom(this.xScale)
                .tickFormat(val => this.xAxis.percent ? `${Number(val).toFixed(2)}%` : Number(val).toFixed(2))
                .tickSizeInner(-5)
                .tickSizeOuter(5)
                .tickPadding(10)
                .tickValues([this.xAxis.min, (this.xAxis.max + this.xAxis.min) / 2, this.xAxis.max]);
            let yAxis = axisLeft(this.yScale)
                .tickFormat(val => this.yAxis.percent ? `${Number(val).toFixed(2)}%` : Number(val).toFixed(2))
                .tickValues([this.yAxis.min, this.yAxis.max])
                .tickSizeInner(-5)
                .tickSizeOuter(5)
                .tickPadding(10);
            // Append our axes
            let xLine = this.quadrantChart.append('g')
                .attr('transform', `translate(0, ${this.height - (this.gutters.top + this.padding.top)})`) // we position from top left
                .attr('class', 'axis-colour')
                .style('font-size', 14)
                .call(xAxis);
            let yLine = this.quadrantChart.append('g')
                .attr('transform', `translate(${this.gutters.left - this.padding.left}, 0)`)
                .attr('class', 'axis-colour')
                .style('font-size', 14)
                .call(yAxis);
            // Restyle axis colours
            xLine.selectAll('path, g.tick line').style('stroke', '#95a6ac');
            yLine.selectAll('path, g.tick line').style('stroke', '#95a6ac');
            // Explicitly set the font sizes for the axis ticks here: firefox will fall back to 10px otherwise
            xLine.selectAll('path, g.tick text').style('font-size', '14px');
            yLine.selectAll('path, g.tick text').style('font-size', '14px');
        },
        handleMouseOver(index) {
            if (this.dots[index].hidden)
                return;
            this.$emit('hover', index);
        },
        handleMouseOut(index) {
            if (this.dots[index].hidden)
                return;
            this.$emit('hover', null);
        },
    }
});
